import moment from 'moment-timezone';
import { CotizacionData, ItemCotizacionData, ItemOfertaData } from '../types';

export interface ValidationErrorInfo {
  message: string;
  focusFieldId?: string;
  itemIndex?: number;
}

export function precioNeto (precioBruto: number, descuento: number) {
  return (precioBruto -(precioBruto * descuento)/100);
}

export function validarCantidadContraPedido(
  cantidad: number,
  maxUnidadesPedido: number,
): string | null {
  if (cantidad > maxUnidadesPedido) {
    return `Las unidades no pueden ser mayores a las solicitadas en el pedido (${maxUnidadesPedido})`;
  }
  return null;
}

export function obtenerItemPedido(
  itemOferta: ItemOfertaData,
  data: CotizacionData | null,
): ItemCotizacionData | null {
  if (!data?.items?.length) {
    return null;
  }

  return (
    data.items.find(
      (i) =>
        String(i.numeroItem) === itemOferta.codigo_item ||
        i.codigoProducto === itemOferta.codigo_producto,
    ) ?? null
  );
}

export function obtenerMaxUnidadesItem(
  itemOferta: ItemOfertaData,
  data: CotizacionData | null,
): number | null {
  return obtenerItemPedido(itemOferta, data)?.totalUnidadesSolicitadas ?? null;
}

export function obtenerNombreProductoItem(
  itemOferta: ItemOfertaData,
  data: CotizacionData | null,
): string {
  const itemPedido = obtenerItemPedido(itemOferta, data);
  if (itemPedido?.nombreProducto) {
    return itemPedido.nombreProducto;
  }
  if (itemOferta.codigo_producto) {
    return itemOferta.codigo_producto;
  }
  return 'Producto';
}

export function formatItemContext(
  item: ItemOfertaData,
  data: CotizacionData | null,
  itemIndex?: number,
): string {
  const nombre = obtenerNombreProductoItem(item, data);
  if (itemIndex !== undefined && itemIndex >= 0) {
    return `Ítem ${itemIndex + 1} (${nombre})`;
  }
  return nombre;
}

export function buildValidationError(
  message: string,
  focusFieldId: string,
  itemIndex?: number,
): string {
  let result = `${message}|FOCUS:${focusFieldId}`;
  if (itemIndex !== undefined && itemIndex >= 0) {
    result += `|INDEX:${itemIndex}`;
  }
  return result;
}

export function prefixItemValidationMessage(
  message: string,
  item: ItemOfertaData,
  data: CotizacionData | null,
  itemIndex?: number,
): string {
  return `${formatItemContext(item, data, itemIndex)}: ${message}`;
}

export function parseValidationError(validationError: string): ValidationErrorInfo {
  const segments = validationError.split('|');
  const message = segments[0];
  let focusFieldId: string | undefined;
  let itemIndex: number | undefined;

  for (const segment of segments.slice(1)) {
    if (segment.startsWith('FOCUS:')) {
      focusFieldId = segment.slice('FOCUS:'.length).trim();
    } else if (segment.startsWith('INDEX:')) {
      const parsed = Number(segment.slice('INDEX:'.length));
      if (!Number.isNaN(parsed)) {
        itemIndex = parsed;
      }
    }
  }

  return { message, focusFieldId, itemIndex };
}

export function focusValidationField(
  focusFieldId: string,
  itemIndex: number,
  delayMs = 300,
): void {
  setTimeout(() => {
    document
      .getElementById(`item-form-${itemIndex}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const fieldElement = document.getElementById(`${focusFieldId}-${itemIndex}`);
    if (fieldElement) {
      fieldElement.focus();
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, delayMs);
}

export function applyValidationErrorFeedback(validationError: string): ValidationErrorInfo {
  const parsed = parseValidationError(validationError);
  if (parsed.focusFieldId !== undefined && parsed.itemIndex !== undefined) {
    focusValidationField(parsed.focusFieldId, parsed.itemIndex);
  }
  return parsed;
}

export function validarCamposRequeridos (
  precioBruto: number, 
  tipoEntrega: string, 
  validoHasta: string, 
  fechaEntrega: string, 
  unidadesDisponibles: number, 
  data: CotizacionData | null
) {
  const camposRequeridos = [
    { campo: precioBruto, nombre: 'Precio Bruto' },
    { campo: tipoEntrega, nombre: 'Tipo Entrega' },
    { campo: validoHasta, nombre: 'Válido Hasta' },
    { campo: unidadesDisponibles, nombre: 'Unidades Disponibles' }
  ];

  // Si el tipo de entrega es "op2" (Plazo), agregar validación de fecha de entrega
  if (tipoEntrega === 'op2') {
    camposRequeridos.push({ campo: fechaEntrega, nombre: 'Fecha Entrega' });
  }

  const camposInvalidos = camposRequeridos.filter(item => {
    const tipoCampo = typeof item.campo;
    if (tipoCampo === 'number') {
      return !item.campo || (item.campo as number) <= 0;
    } else {
      return !String(item.campo).trim();
    }
  });
    
  if (camposInvalidos.length > 0) {
    const nombresCampos = camposInvalidos.map(item => item.nombre).join(', ');
    return `Los siguientes campos son requeridos: ${nombresCampos}`;
  }

  // Validar fecha válido hasta
  const errorFecha = validarFecha(validoHasta, data);
  if (errorFecha) {
    return errorFecha;
  }

  // Validar fecha de entrega si es requerida
  if (tipoEntrega === 'op2') {
    const errorFechaEntrega = validarFecha(fechaEntrega, data);
    if (errorFechaEntrega) {
      return errorFechaEntrega;
    }
  }
    
  return null;
}

export function validarItemIndividual(
  item: ItemOfertaData,
  data: CotizacionData | null,
  itemIndex?: number,
): string | null {
  const camposRequeridos = [
    { campo: item.precio_bruto, nombre: 'Precio Bruto', id: 'grossAmount' },
    { campo: item.tipo_entrega, nombre: 'Tipo Entrega', id: 'deliveryType' },
    { campo: item.valido_hasta, nombre: 'Válido Hasta', id: 'validoHasta' },
    { campo: item.cantidad, nombre: 'Unidades Disponibles', id: 'unidadesDisponibles' }
  ];

  // Si el tipo de entrega es "op2" (Plazo), agregar validación de fecha de entrega
  if (item.tipo_entrega === 'op2') {
    camposRequeridos.push({ campo: item.fecha_entrega, nombre: 'Fecha Entrega', id: 'deliveryDate' });
  }

  const camposInvalidos = camposRequeridos.filter(campo => {
    const tipoCampo = typeof campo.campo;
    if (tipoCampo === 'number') {
      return !campo.campo || (campo.campo as number) <= 0;
    } else {
      return !String(campo.campo).trim();
    }
  });
    
  if (camposInvalidos.length > 0) {
    const nombresCampos = camposInvalidos.map(campo => campo.nombre).join(', ');
    const primerCampoFaltante = camposInvalidos[0];
    const message = prefixItemValidationMessage(
      `Los siguientes campos son requeridos: ${nombresCampos}`,
      item,
      data,
      itemIndex,
    );
    return buildValidationError(message, primerCampoFaltante.id, itemIndex);
  }

  // Validar fecha válido hasta (usar directamente el campo valido_hasta)
  if (item.valido_hasta) {
    const errorFecha = validarFecha(item.valido_hasta, data);
    if (errorFecha) {
      return prefixItemValidationMessage(errorFecha, item, data, itemIndex);
    }
  }

  // Validar fecha de entrega si es requerida
  if (item.tipo_entrega === 'op2') {
    const errorFechaEntrega = validarFecha(item.fecha_entrega, data);
    if (errorFechaEntrega) {
      return buildValidationError(
        prefixItemValidationMessage(errorFechaEntrega, item, data, itemIndex),
        'deliveryDate',
        itemIndex,
      );
    }
  }

  const maxUnidades = obtenerMaxUnidadesItem(item, data);
  if (maxUnidades !== null) {
    const errorCantidad = validarCantidadContraPedido(item.cantidad, maxUnidades);
    if (errorCantidad) {
      return buildValidationError(
        prefixItemValidationMessage(errorCantidad, item, data, itemIndex),
        'unidadesDisponibles',
        itemIndex,
      );
    }
  }
    
  return null;
}

export function validarFecha (fechaSeleccionada: string, _data: CotizacionData | null): string | null{
  if (!fechaSeleccionada) return null;
    
  // Obtener la fecha de hoy en la zona horaria de Bogotá al inicio del día
  const hoy = moment().tz('America/Bogota').startOf('day');
  // Parsear la fecha directamente en la zona horaria de Bogotá usando el formato exacto
  const fechaSeleccionadaMoment = moment.tz(fechaSeleccionada, 'YYYY-MM-DD', 'America/Bogota').startOf('day');
    
  // Comparar usando isSameOrAfter para incluir el día de hoy
  if (!fechaSeleccionadaMoment.isSameOrAfter(hoy)) {
    return 'La fecha debe ser mayor o igual a hoy';
  }
    
  return null;
}

/**
 * Verifica si un item está lleno (tiene datos válidos)
 * Un item se considera lleno si tiene precio_bruto > 0, tipo_entrega, valido_hasta y cantidad > 0
 */
export function isItemFilled(item: ItemOfertaData): boolean {
  // Verificar precio bruto
  if (!item.precio_bruto || item.precio_bruto <= 0) {
    return false;
  }

  // Verificar tipo de entrega
  if (!item.tipo_entrega || item.tipo_entrega.trim() === '') {
    return false;
  }

  // Verificar valido_hasta (campo independiente)
  if (!item.valido_hasta || item.valido_hasta.trim() === '') {
    return false;
  }

  // Verificar cantidad
  if (!item.cantidad || item.cantidad <= 0) {
    return false;
  }

  // Si el tipo de entrega es "op2" (Plazo), verificar fecha de entrega
  if (item.tipo_entrega === 'op2') {
    if (!item.fecha_entrega || item.fecha_entrega.trim() === '') {
      return false;
    }
  }

  return true;
}